/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { 
  useFetchDerivativeSecurities,
  useCreateDerivativeSecurity, 
  useUpdateDerivativeSecurity, 
  useDeleteDerivativeSecurity
} from '@derivativeSecurityHooks';
import { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { 
  PageHeaderSkeleton, 
  AccountCardSkeleton 
} from '@/components/LoadingSkeletons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ArrowDownToLine, ArrowUpFromLine, Plus, AlertCircle, Edit, Trash2, Calendar, TrendingUp, TrendingDown, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { DerivativeForm } from '@/components/derivative-form/DerivativeForm';
import { calculateDaysUntilExpiration, isNearExpiry } from '@securityUtils';
import { calculateOptionProfitLoss, calculateFuturesProfitLoss } from '@derivativeSecurityUtils';
import { SecuritiesTransactionForm, type SecuritiesTransactionFormData } from '@/components/securities-transaction-form/SecuritiesTransactionForm';
import { useFetchAccounts } from '@accountHooks';
import { useCreateSecuritiesTransaction } from '@/hooks/securities/useSecuritiesTransactions.ts';
import { SecuritiesErrorState } from '@/components/securities-error-state/SecuritiesErrorState';
import {DerivativeSecurity} from "@/integrations/supabase/derivative.models.ts";

/**
 * DerivativesPage component displays and manages derivative securities (options and futures).
 * This is a dedicated page for derivatives accessible at /investments/securities/derivatives.
 */
export default function DerivativesPage() {
  // Dialog state
  const [derivativeFormOpen, setDerivativeFormOpen] = useState(false);
  const [transactionFormOpen, setTransactionFormOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<'deposit' | 'withdrawal'>('deposit');
  const [editingDerivative, setEditingDerivative] = useState<DerivativeSecurity | null>(null);
  const [deletingDerivativeId, setDeletingDerivativeId] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch derivative securities
  const { 
    data: derivatives = [], 
    isLoading, 
    error,
    refetch,
  } = useFetchDerivativeSecurities();

  const filteredDerivatives = useMemo(() => {
    if (!searchQuery) return derivatives;
    const lowerQuery = searchQuery.toLowerCase();
    return derivatives.filter(d => 
      d.underlying_asset?.toLowerCase().includes(lowerQuery) ||
      d.derivative_type?.toLowerCase().includes(lowerQuery) ||
      d.account_number?.toLowerCase().includes(lowerQuery) ||
      d.member_number?.toLowerCase().includes(lowerQuery)
    );
  }, [derivatives, searchQuery]);

  // Fetch accounts for transaction form
  const { data: accounts = [] } = useFetchAccounts();

  // Mutation hooks
  const createDerivative = useCreateDerivativeSecurity();
  const updateDerivative = useUpdateDerivativeSecurity();
  const deleteDerivative = useDeleteDerivativeSecurity();
  const createTransaction = useCreateSecuritiesTransaction();

  // Handler functions for action buttons
  const handleAddDerivative = () => {
    setEditingDerivative(null);
    setDerivativeFormOpen(true);
  };

  const handleDeposit = () => {
    setTransactionType('deposit');
    setTransactionFormOpen(true);
  };

  const handleWithdraw = () => {
    setTransactionType('withdrawal');
    setTransactionFormOpen(true);
  };

  const handleEditDerivative = (derivative: DerivativeSecurity) => {
    setEditingDerivative(derivative);
    setDerivativeFormOpen(true);
  };

  const handleDeleteDerivative = (derivativeId: string) => {
    setDeletingDerivativeId(derivativeId);
  };

  // Handler for saving derivative (create or update)
  const handleSaveDerivative = async (data: any) => {
    try {
      if (editingDerivative) {
        // Update existing derivative
        await updateDerivative.mutateAsync({
          id: editingDerivative.id,
          ...data,
        });
        toast.success('Derivative security updated successfully');
        setStatusMessage('Derivative security updated successfully');
      } else {
        // Create new derivative
        await createDerivative.mutateAsync(data);
        toast.success('Derivative security created successfully');
        setStatusMessage('Derivative security created successfully');
      }
      setDerivativeFormOpen(false);
      setEditingDerivative(null);
    } catch (error) {
      console.error('Error saving derivative:', error);
      const msg = editingDerivative ? 'Failed to update derivative security' : 'Failed to create derivative security';
      toast.error(msg);
      setStatusMessage(msg);
    }
  };

  // Handler for canceling derivative form
  const handleCancelDerivativeForm = () => {
    setDerivativeFormOpen(false);
    setEditingDerivative(null);
  };

  // Handler for confirming delete
  const handleConfirmDelete = async () => {
    if (!deletingDerivativeId) return;
    
    try {
      await deleteDerivative.mutateAsync(deletingDerivativeId);
      toast.success('Derivative security deleted successfully');
      setStatusMessage('Derivative security deleted successfully');
      setDeletingDerivativeId(null);
    } catch (error) {
      console.error('Error deleting derivative:', error);
      toast.error('Failed to delete derivative security');
      setStatusMessage('Failed to delete derivative security');
    }
  };

  // Handler for canceling delete
  const handleCancelDelete = () => {
    setDeletingDerivativeId(null);
  };

  // Handler for saving transaction
  const handleSaveTransaction = async (data: SecuritiesTransactionFormData) => {
    try {
      await createTransaction.mutateAsync({
        security_type: data.security_type,
        security_id: data.security_id,
        account_id: data.account_id,
        transaction_type: data.transaction_type,
        amount: data.amount,
        currency: data.currency,
        transaction_date: data.transaction_date,
        notes: data.notes || null,
      });
      const msg = `${data.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal'} recorded successfully`;
      toast.success(msg);
      setStatusMessage(msg);
      setTransactionFormOpen(false);
    } catch (error) {
      console.error('Error recording transaction:', error);
      toast.error('Failed to record transaction');
      setStatusMessage('Failed to record transaction');
    }
  };

  // Handler for canceling transaction form
  const handleCancelTransactionForm = () => {
    setTransactionFormOpen(false);
  };

  // Disable deposit/withdraw buttons when no derivatives exist
  const hasDerivatives = derivatives.length > 0;

  // Prepare securities list for transaction form
  const securitiesForTransaction = derivatives.map(derivative => ({
    id: derivative.id,
    label: `${derivative.derivative_type.toUpperCase()} - ${derivative.underlying_asset}`,
    currency: derivative.currency,
  }));

  // Show loading skeletons while data is loading
  if (isLoading) {
    return (
      <ErrorBoundary
        onReset={() => {
          window.location.reload();
        }}
      >
        <div className="space-y-6" aria-busy="true" aria-live="polite">
          <PageHeaderSkeleton />
          <div className="space-y-4">
            <AccountCardSkeleton />
            <AccountCardSkeleton />
            <AccountCardSkeleton />
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  // Show error state
  if (error) {
    return (
      <ErrorBoundary onReset={() => { window.location.reload(); }}>
        <div className="space-y-6">
          <SecuritiesErrorState
            message="Failed to load derivative securities. Please try again."
            onRetry={() => refetch()}
          />
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      onReset={() => {
        window.location.reload();
      }}
    >
      <div className="space-y-6">
        {/* Screen reader live region for mutation status announcements */}
        <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
          {statusMessage}
        </div>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Derivatives</h1>
            <p className="text-sm text-muted-foreground sm:text-base">
              Manage your options and futures contracts
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:flex-wrap w-full lg:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search derivatives..."
                className="pl-8"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row w-full sm:w-auto" role="group" aria-label="Derivatives actions">
              <Button
                onClick={handleDeposit}
                disabled={!hasDerivatives}
                variant="outline"
                aria-label="Deposit funds to derivatives account"
                aria-disabled={!hasDerivatives}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowDownToLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Deposit
              </Button>
              <Button
                onClick={handleWithdraw}
                disabled={!hasDerivatives}
                variant="outline"
                aria-label="Withdraw funds from derivatives account"
                aria-disabled={!hasDerivatives}
                className="min-h-[44px] w-full sm:w-auto"
              >
                <ArrowUpFromLine className="mr-2 h-4 w-4" aria-hidden="true" />
                Withdraw
              </Button>
              <Button
                onClick={handleAddDerivative}
                aria-label="Add new derivative security"
                className="min-h-[44px] w-full sm:w-auto"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Derivative Cards List or Empty State */}
        {hasDerivatives ? (
          <div 
            className="space-y-4" 
            role="list" 
            aria-label="Derivative securities"
            aria-live="polite"
          >
            {filteredDerivatives.map((derivative) => {
              const daysUntilExpiration = calculateDaysUntilExpiration(derivative.expiration_date);
              const nearExpiry = isNearExpiry(derivative.expiration_date);

              // Calculate profit/loss based on derivative type
              // Note: For real implementation, current_underlying_price and current_price would come from API
              const currentUnderlyingPrice = 100; // Placeholder
              const currentPrice = 100; // Placeholder
              
              let profitLoss;
              if (derivative.derivative_type === 'option') {
                profitLoss = calculateOptionProfitLoss(
                  derivative.option_type! as DerivativeOptionType,
                  derivative.strike_price!,
                  derivative.premium_paid!,
                  currentUnderlyingPrice,
                  derivative.contract_size
                );
              } else {
                profitLoss = calculateFuturesProfitLoss(
                  derivative.contract_price!,
                  currentPrice,
                  derivative.contract_size
                );
              }

              const isProfit = profitLoss.profitLoss >= 0;

              return (
                <Card key={derivative.id} className="hover:bg-accent/50 transition-colors">
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col gap-4">
                      {/* Header Row */}
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold">
                              {derivative.underlying_asset}
                            </h3>
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded">
                              {derivative.derivative_type === 'option' 
                                ? `${derivative.option_type?.toUpperCase()} Option` 
                                : 'Future'}
                            </span>
                            {nearExpiry && (
                              <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 rounded">
                                Expiring Soon
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">
                            {derivative.exchange}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEditDerivative(derivative)}
                            aria-label={`Edit ${derivative.underlying_asset} ${derivative.derivative_type}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Edit className="h-4 w-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteDerivative(derivative.id)}
                            aria-label={`Delete ${derivative.underlying_asset} ${derivative.derivative_type}`}
                            className="min-h-[44px] min-w-[44px]"
                          >
                            <Trash2 className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        </div>
                      </div>

                      {/* Metrics Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {derivative.derivative_type === 'option' ? (
                          <>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Strike Price</p>
                              <p className="text-sm font-medium">{derivative.currency} {derivative.strike_price?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Premium Paid</p>
                              <p className="text-sm font-medium">{derivative.currency} {derivative.premium_paid?.toFixed(2)}</p>
                            </div>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Contract Price</p>
                              <p className="text-sm font-medium">{derivative.currency} {derivative.contract_price?.toFixed(2)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground mb-1">Margin Required</p>
                              <p className="text-sm font-medium">{derivative.currency} {derivative.margin_requirement?.toFixed(2)}</p>
                            </div>
                          </>
                        )}
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Contract Size</p>
                          <p className="text-sm font-medium">{derivative.contract_size.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })}</p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Profit/Loss</p>
                          <div className="flex items-center gap-1">
                            <p className={`text-sm font-medium ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                              {isProfit ? '+' : ''}{derivative.currency} {profitLoss.profitLoss.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {isProfit ? (
                              <TrendingUp className="h-3 w-3 text-emerald-600" aria-hidden="true" />
                            ) : (
                              <TrendingDown className="h-3 w-3 text-red-600" aria-hidden="true" />
                            )}
                          </div>
                          <p className={`text-xs ${isProfit ? 'text-emerald-600' : 'text-red-600'}`}>
                            {isProfit ? '+' : ''}{profitLoss.profitLossPercentage.toFixed(2)}%
                          </p>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" aria-hidden="true" />
                          <span>Expiration: {new Date(derivative.expiration_date).toLocaleDateString()}</span>
                        </div>
                        <span>• {daysUntilExpiration} days until expiration</span>
                        {derivative.notes && <span>• {derivative.notes}</span>}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card role="status" aria-label="Empty state">
            <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <p className="text-base sm:text-lg text-muted-foreground mb-4">
                No derivative securities yet
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6">
                Get started by adding your first options or futures contract
              </p>
              <Button 
                onClick={handleAddDerivative} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Add your first derivative security"
              >
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add Investment
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Add/Edit Derivative Dialog */}
        <Dialog open={derivativeFormOpen} onOpenChange={setDerivativeFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="derivative-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {editingDerivative ? 'Edit Derivative Security' : 'Add Derivative Security'}
              </DialogTitle>
              <DialogDescription id="derivative-dialog-description">
                {editingDerivative 
                  ? 'Update the details of your derivative contract.' 
                  : 'Add a new options or futures contract to your portfolio.'}
              </DialogDescription>
            </DialogHeader>
            <DerivativeForm
              derivative={editingDerivative}
              onSave={handleSaveDerivative}
              onCancel={handleCancelDerivativeForm}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deletingDerivativeId} onOpenChange={(open) => !open && handleCancelDelete()}>
          <AlertDialogContent 
            className="w-[95vw] sm:w-full sm:max-w-[500px]"
            aria-describedby="delete-dialog-description"
          >
            <AlertDialogHeader>
              <AlertDialogTitle>Are you sure?</AlertDialogTitle>
              <AlertDialogDescription id="delete-dialog-description">
                This action cannot be undone. This will permanently delete the derivative security
                and all associated transaction history.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="flex-col gap-2 sm:flex-row">
              <AlertDialogCancel 
                onClick={handleCancelDelete} 
                className="min-h-[44px] w-full sm:w-auto"
                aria-label="Cancel deletion"
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDelete}
                className="min-h-[44px] w-full sm:w-auto bg-destructive text-destructive-foreground hover:bg-destructive/90"
                aria-label="Confirm deletion of derivative security"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transaction Dialog */}
        <Dialog open={transactionFormOpen} onOpenChange={setTransactionFormOpen}>
          <DialogContent 
            className="max-h-[90vh] overflow-y-auto sm:max-w-[600px] w-[95vw] sm:w-full"
            aria-describedby="transaction-dialog-description"
          >
            <DialogHeader>
              <DialogTitle>
                {transactionType === 'deposit' ? 'Deposit to Derivatives Account' : 'Withdraw from Derivatives Account'}
              </DialogTitle>
              <DialogDescription id="transaction-dialog-description">
                {transactionType === 'deposit' 
                  ? 'Record a deposit to your derivatives account.' 
                  : 'Record a withdrawal from your derivatives account.'}
              </DialogDescription>
            </DialogHeader>
            <SecuritiesTransactionForm
              transactionType={transactionType}
              securityType="derivative"
              securities={securitiesForTransaction}
              accounts={accounts}
              onSave={handleSaveTransaction}
              onCancel={handleCancelTransactionForm}
            />
          </DialogContent>
        </Dialog>
      </div>
    </ErrorBoundary>
  );
}
